import React, { useState, useEffect, useCallback, useRef } from 'react';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import { SharedStyles as Styles } from '@screens/Authenticated/Property/sharedStyles';
import { useUserStore } from '@stores/User/User';
import {
  IRole,
  IUser,
  CreateUserPayload,
  UpdateUserPayload,
  CreateRolePayload,
  UpdateRolePayload,
  AVAILABLE_PERMISSIONS,
  Permission
} from '@stores/User/types';
import {
  Box,
  Stack,
  Avatar,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Button,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

// ─── Constants ────────────────────────────────────────────────────────────────

const DISPLAY_USER_ROLES = false;

const PERMISSION_LABELS: Record<Permission, string> = {
  incluir: 'Incluir',
  consultar: 'Consultar',
  editar: 'Editar',
  publicar: 'Publicar',
  excluir: 'Excluir'
};

const DEFAULT_ROLE_FORM: CreateRolePayload = {
  name: '',
  permissions: [],
  active: true
};

const DEFAULT_USER_FORM: CreateUserPayload = {
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
  role_id: 0,
  creci: '',
  cellphone: '',
  office_phone: ''
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionTitle: React.FC<{ icon: React.ReactNode; label: string }> = ({
  icon,
  label
}) => (
  <Box display="flex" alignItems="center" gap={1} mb={1}>
    {icon}
    <Typography variant="h6" fontWeight={700} color="#125dae">
      {label}
    </Typography>
  </Box>
);

const tableHeaderStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 13,
  color: '#555',
  borderBottom: '2px solid #e0e0e0',
  background: '#f7f9fc'
};

const tableCellStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  borderBottom: '1px solid #ececec',
  verticalAlign: 'middle'
};

// ─── Role Modal ───────────────────────────────────────────────────────────────

interface RoleModalProps {
  open: boolean;
  editingRole: IRole | null;
  onClose: () => void;
  onSave: (payload: CreateRolePayload | UpdateRolePayload) => Promise<boolean>;
  saving: boolean;
}

const RoleModal: React.FC<RoleModalProps> = ({
  open,
  editingRole,
  onClose,
  onSave,
  saving
}) => {
  const [form, setForm] = useState<CreateRolePayload>(DEFAULT_ROLE_FORM);

  useEffect(() => {
    if (editingRole) {
      setForm({
        name: editingRole.name,
        permissions: [...editingRole.permissions],
        active: editingRole.active
      });
    } else {
      setForm(DEFAULT_ROLE_FORM);
    }
  }, [editingRole, open]);

  const handlePermissionToggle = (perm: Permission) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const handleSubmit = async () => {
    const ok = await onSave(form);
    if (ok) onClose();
  };

  const isProtected = editingRole?.protected ?? false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#125dae' }}>
        {editingRole ? 'Editar Papel de Usuário' : 'Novo Papel de Usuário'}
        {isProtected && (
          <Chip
            icon={<LockIcon />}
            label="Protegido"
            size="small"
            color="warning"
            sx={{ ml: 1 }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} mt={0.5}>
          <TextField
            label="Nome do Papel"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            disabled={isProtected}
            fullWidth
            size="small"
            required
          />

          <Box>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Permissões
            </Typography>
            <FormGroup row>
              {AVAILABLE_PERMISSIONS.map((perm) => (
                <FormControlLabel
                  key={perm}
                  control={
                    <Checkbox
                      checked={form.permissions.includes(perm)}
                      onChange={() => handlePermissionToggle(perm)}
                      disabled={isProtected}
                      size="small"
                      sx={{
                        color: '#125dae',
                        '&.Mui-checked': { color: '#125dae' }
                      }}
                    />
                  }
                  label={PERMISSION_LABELS[perm]}
                />
              ))}
            </FormGroup>
          </Box>

          {!isProtected && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.active}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, active: e.target.checked }))
                  }
                  size="small"
                  sx={{
                    color: '#125dae',
                    '&.Mui-checked': { color: '#125dae' }
                  }}
                />
              }
              label="Ativo"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving || !form.name.trim() || isProtected}
          variant="contained"
          sx={{ bgcolor: '#125dae', '&:hover': { bgcolor: '#0e4a8a' } }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── User Modal ───────────────────────────────────────────────────────────────

interface UserModalProps {
  open: boolean;
  editingUser: IUser | null;
  roles: IRole[];
  onClose: () => void;
  onSave: (payload: CreateUserPayload | UpdateUserPayload) => Promise<boolean>;
  saving: boolean;
}

const UserModal: React.FC<UserModalProps> = ({
  open,
  editingUser,
  roles,
  onClose,
  onSave,
  saving
}) => {
  const { uploadUserAvatar } = useUserStore();
  const [form, setForm] = useState<CreateUserPayload>(DEFAULT_USER_FORM);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingUser) {
      setForm({
        name: editingUser.name,
        email: editingUser.email,
        password: '',
        password_confirmation: '',
        role_id: editingUser.role_id,
        creci: editingUser.creci ?? '',
        cellphone: editingUser.cellphone ?? '',
        office_phone: editingUser.office_phone ?? ''
      });
    } else {
      setForm(DEFAULT_USER_FORM);
    }
    setAvatarFile(null);
    setAvatarPreview(null);
  }, [editingUser, open]);

  const handleChange = (
    field: keyof CreateUserPayload,
    value: string | number
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    // reset input so the same file can be re-selected
    e.target.value = '';
  };

  const isAdminUser = editingUser?.role?.protected ?? false;

  const handleSubmit = async () => {
    let avatarUrl: string | undefined = undefined;
    if (avatarFile) {
      setUploadingAvatar(true);
      const url = await uploadUserAvatar(avatarFile);
      setUploadingAvatar(false);
      if (url) avatarUrl = url;
    }

    let payload: CreateUserPayload | UpdateUserPayload;

    if (editingUser) {
      // Build only changed fields for update
      const update: UpdateUserPayload = {};
      if (form.name !== editingUser.name) update.name = form.name;
      if (form.email !== editingUser.email) update.email = form.email;
      if (form.password) update.password = form.password;
      if (!isAdminUser && form.role_id !== editingUser.role_id)
        update.role_id = Number(form.role_id);
      if (form.creci !== (editingUser.creci ?? '')) update.creci = form.creci;
      if (form.cellphone !== (editingUser.cellphone ?? ''))
        update.cellphone = form.cellphone;
      if (form.office_phone !== (editingUser.office_phone ?? ''))
        update.office_phone = form.office_phone;
      if (avatarUrl) update.avatar = avatarUrl;
      payload = update;
    } else {
      payload = { ...form, role_id: Number(form.role_id) };
      if (avatarUrl) (payload as CreateUserPayload).avatar = avatarUrl;
    }

    const ok = await onSave(payload);
    if (ok) onClose();
  };

  const isCreateMode = !editingUser;
  const canSubmit =
    form.name.trim() &&
    form.email.trim() &&
    form.role_id > 0 &&
    (!isCreateMode ||
      (form.password.length >= 8 &&
        form.password === form.password_confirmation));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#125dae' }}>
        {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        {isAdminUser && (
          <Chip
            icon={<LockIcon />}
            label="Admin Protegido"
            size="small"
            color="warning"
            sx={{ ml: 1 }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} mt={0.5}>
          {/* ── Avatar Picker ── */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}>
            <Box position="relative" sx={{ width: 88, height: 88 }}>
              <Avatar
                src={avatarPreview ?? editingUser?.avatar ?? undefined}
                sx={{
                  width: 88,
                  height: 88,
                  cursor: 'pointer',
                  border: '3px solid #e0e0e0',
                  fontSize: 36
                }}
                onClick={() => fileInputRef.current?.click()}
              />
              <Box
                position="absolute"
                bottom={0}
                right={0}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  bgcolor: '#125dae',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px solid white',
                  '&:hover': { bgcolor: '#0e4a8a' }
                }}>
                <CameraAltIcon sx={{ fontSize: 15, color: 'white' }} />
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {avatarFile ? avatarFile.name : 'Clique para alterar a foto'}
            </Typography>
          </Box>

          <TextField
            label="Nome Completo"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            size="small"
            required
          />
          <TextField
            label="E-mail"
            type="email"
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            fullWidth
            size="small"
            required
          />
          <TextField
            label={
              isCreateMode
                ? 'Senha (mín. 8 caracteres)'
                : 'Nova Senha (deixe em branco para não alterar)'
            }
            type="password"
            value={form.password}
            onChange={(e) => handleChange('password', e.target.value)}
            fullWidth
            size="small"
            required={isCreateMode}
          />
          {(isCreateMode || form.password) && (
            <TextField
              label="Confirmar Senha"
              type="password"
              value={form.password_confirmation}
              onChange={(e) =>
                handleChange('password_confirmation', e.target.value)
              }
              fullWidth
              size="small"
              required={isCreateMode}
              error={
                !!form.password_confirmation &&
                form.password !== form.password_confirmation
              }
              helperText={
                form.password_confirmation &&
                form.password !== form.password_confirmation
                  ? 'As senhas não coincidem'
                  : ''
              }
            />
          )}

          <FormControl size="small" fullWidth required disabled={isAdminUser}>
            <InputLabel>Papel de Usuário</InputLabel>
            <Select
              value={form.role_id || ''}
              label="Papel de Usuário"
              onChange={(e) => handleChange('role_id', Number(e.target.value))}>
              {roles
                .filter((r) => r.active || r.id === editingUser?.role_id)
                .map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name.charAt(0).toUpperCase() + r.name.slice(1)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Divider />

          <TextField
            label="CRECI"
            value={form.creci}
            onChange={(e) => handleChange('creci', e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Celular"
            value={form.cellphone}
            onChange={(e) => handleChange('cellphone', e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Telefone Comercial"
            value={form.office_phone}
            onChange={(e) => handleChange('office_phone', e.target.value)}
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving || uploadingAvatar || !canSubmit}
          variant="contained"
          sx={{ bgcolor: '#125dae', '&:hover': { bgcolor: '#0e4a8a' } }}>
          {saving || uploadingAvatar ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Salvar'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

const ConfigUsersScreen: React.FC = () => {
  const {
    adminUsers,
    roles,
    fetchUsers,
    fetchRoles,
    createUser,
    updateUser,
    toggleUser,
    createRole,
    updateRole,
    toggleRole
  } = useUserStore();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Role modal state
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<IRole | null>(null);

  // User modal state
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<IUser | null>(null);

  // Feedback state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const showFeedback = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchRoles(), fetchUsers()]);
    setLoading(false);
  }, [fetchRoles, fetchUsers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Role handlers ─────────────────────────────────────────────────────────

  const handleOpenRoleModal = (role?: IRole) => {
    setEditingRole(role ?? null);
    setRoleModalOpen(true);
  };

  const handleSaveRole = async (
    payload: CreateRolePayload | UpdateRolePayload
  ): Promise<boolean> => {
    setSaving(true);
    try {
      let ok: boolean;
      if (editingRole) {
        ok = await updateRole(editingRole.id, payload as UpdateRolePayload);
      } else {
        ok = await createRole(payload as CreateRolePayload);
      }
      if (ok) {
        showFeedback(
          editingRole
            ? 'Papel atualizado com sucesso!'
            : 'Papel criado com sucesso!',
          'success'
        );
      } else {
        showFeedback('Erro ao salvar papel. Tente novamente.', 'error');
      }
      return ok;
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRole = async (role: IRole) => {
    if (role.protected) return;
    setSaving(true);
    const ok = await toggleRole(role.id);
    setSaving(false);
    if (ok) {
      showFeedback(
        role.active ? 'Papel inativado.' : 'Papel ativado.',
        'success'
      );
    } else {
      showFeedback('Erro ao alterar estado do papel.', 'error');
    }
  };

  // ─── User handlers ─────────────────────────────────────────────────────────

  const handleOpenUserModal = (user?: IUser) => {
    setEditingUser(user ?? null);
    setUserModalOpen(true);
  };

  const handleSaveUser = async (
    payload: CreateUserPayload | UpdateUserPayload
  ): Promise<boolean> => {
    setSaving(true);
    try {
      let ok: boolean;
      if (editingUser) {
        ok = await updateUser(editingUser.id, payload as UpdateUserPayload);
      } else {
        ok = await createUser(payload as CreateUserPayload);
      }
      if (ok) {
        showFeedback(
          editingUser
            ? 'Usuário atualizado com sucesso!'
            : 'Usuário criado com sucesso!',
          'success'
        );
      } else {
        showFeedback('Erro ao salvar usuário. Tente novamente.', 'error');
      }
      return ok;
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUser = async (user: IUser) => {
    if (user.role?.protected) return;
    setSaving(true);
    const ok = await toggleUser(user.id);
    setSaving(false);
    if (ok) {
      showFeedback(
        user.active ? 'Usuário inativado.' : 'Usuário ativado.',
        'success'
      );
    } else {
      showFeedback('Erro ao alterar estado do usuário.', 'error');
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <AuthenticatedLayout>
      <Styles.PageSection>
        <h1>Gestão de Usuários</h1>
        <Styles.RectangleBar />

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: '#125dae' }} />
          </Box>
        ) : (
          <Stack spacing={5}>
            {/* ── Papéis de Usuário ── */}
            {DISPLAY_USER_ROLES && (
              <Box>
                <SectionTitle
                  icon={<BadgeIcon sx={{ color: '#125dae' }} />}
                  label="Papéis de Usuário"
                />
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden',
                    background: '#fff'
                  }}>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        minWidth: 520,
                        borderCollapse: 'collapse'
                      }}>
                      <thead>
                        <tr>
                          <th style={tableHeaderStyle}>Nome</th>
                          <th style={tableHeaderStyle}>Permissões</th>
                          <th
                            style={{
                              ...tableHeaderStyle,
                              textAlign: 'center'
                            }}>
                            Status
                          </th>
                          <th
                            style={{
                              ...tableHeaderStyle,
                              textAlign: 'center'
                            }}>
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {roles.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              style={{
                                ...tableCellStyle,
                                textAlign: 'center',
                                color: '#999',
                                padding: '24px'
                              }}>
                              Nenhum papel cadastrado
                            </td>
                          </tr>
                        ) : (
                          roles.map((role) => (
                            <tr key={role.id}>
                              <td style={tableCellStyle}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}>
                                  <Typography fontSize={14} fontWeight={600}>
                                    {role.name.charAt(0).toUpperCase() +
                                      role.name.slice(1)}
                                  </Typography>
                                  {role.protected && (
                                    <Tooltip title="Papel protegido – não pode ser inativado">
                                      <LockIcon
                                        sx={{ fontSize: 15, color: '#f59e0b' }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </td>
                              <td style={tableCellStyle}>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {(role.permissions ?? []).map((p) => (
                                    <Chip
                                      key={p}
                                      label={
                                        PERMISSION_LABELS[p as Permission] ?? p
                                      }
                                      size="small"
                                      sx={{
                                        fontSize: 11,
                                        bgcolor: '#e8f0fe',
                                        color: '#125dae',
                                        fontWeight: 600
                                      }}
                                    />
                                  ))}
                                </Box>
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  textAlign: 'center'
                                }}>
                                <Chip
                                  label={role.active ? 'Ativo' : 'Inativo'}
                                  size="small"
                                  sx={{
                                    bgcolor: role.active
                                      ? '#dcfce7'
                                      : '#fee2e2',
                                    color: role.active ? '#166534' : '#991b1b',
                                    fontWeight: 600,
                                    fontSize: 11
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  textAlign: 'center'
                                }}>
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  gap={0.5}>
                                  <Tooltip title="Editar">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenRoleModal(role)}
                                      sx={{ color: '#125dae' }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      role.protected
                                        ? 'Papel protegido'
                                        : role.active
                                          ? 'Inativar'
                                          : 'Ativar'
                                    }>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleToggleRole(role)}
                                        disabled={role.protected || saving}
                                        sx={{
                                          color: role.active
                                            ? '#dc2626'
                                            : '#16a34a'
                                        }}>
                                        {role.active ? (
                                          <BlockIcon fontSize="small" />
                                        ) : (
                                          <CheckCircleOutlineIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenRoleModal()}
                    sx={{
                      bgcolor: '#125dae',
                      '&:hover': { bgcolor: '#0e4a8a' },
                      fontSize: 13,
                      textTransform: 'none',
                      borderRadius: 2
                    }}>
                    Adicionar Papel de Usuário
                  </Button>
                </Box>
              </Box>
            )}

            {/* ── Usuários ── */}
            <Box>
              <SectionTitle
                icon={<PersonIcon sx={{ color: '#125dae' }} />}
                label="Usuários"
              />
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  background: '#fff'
                }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      minWidth: 780,
                      borderCollapse: 'collapse'
                    }}>
                    <thead>
                      <tr>
                        <th style={tableHeaderStyle}>Usuário</th>
                        <th style={tableHeaderStyle}>E-mail</th>
                        <th style={tableHeaderStyle}>Papel</th>
                        <th style={tableHeaderStyle}>CRECI</th>
                        <th style={tableHeaderStyle}>Celular</th>
                        <th
                          style={{ ...tableHeaderStyle, textAlign: 'center' }}>
                          Status
                        </th>
                        <th
                          style={{ ...tableHeaderStyle, textAlign: 'center' }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminUsers.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            style={{
                              ...tableCellStyle,
                              textAlign: 'center',
                              color: '#999',
                              padding: '24px'
                            }}>
                            Nenhum usuário cadastrado
                          </td>
                        </tr>
                      ) : (
                        adminUsers.map((user) => (
                          <tr key={user.id}>
                            <td style={tableCellStyle}>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar
                                  src={user.avatar ?? undefined}
                                  alt={user.name}
                                  sx={{ width: 34, height: 34 }}
                                />
                                <Box>
                                  <Typography fontSize={13} fontWeight={600}>
                                    {user.name}
                                  </Typography>
                                  {user.office_phone && (
                                    <Typography
                                      fontSize={11}
                                      color="text.secondary">
                                      {user.office_phone}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </td>
                            <td style={tableCellStyle}>
                              <Typography fontSize={13}>
                                {user.email}
                              </Typography>
                            </td>
                            <td style={tableCellStyle}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Chip
                                  label={
                                    user.role
                                      ? user.role.name.charAt(0).toUpperCase() +
                                        user.role.name.slice(1)
                                      : `#${user.role_id}`
                                  }
                                  size="small"
                                  sx={{
                                    fontSize: 11,
                                    bgcolor: '#f3f4f6',
                                    fontWeight: 600
                                  }}
                                />
                                {user.role?.protected && (
                                  <Tooltip title="Papel protegido">
                                    <LockIcon
                                      sx={{ fontSize: 14, color: '#f59e0b' }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </td>
                            <td style={tableCellStyle}>
                              <Typography fontSize={13}>
                                {user.creci || '—'}
                              </Typography>
                            </td>
                            <td style={tableCellStyle}>
                              <Typography fontSize={13}>
                                {user.cellphone || '—'}
                              </Typography>
                            </td>
                            <td
                              style={{
                                ...tableCellStyle,
                                textAlign: 'center'
                              }}>
                              <Chip
                                label={user.active ? 'Ativo' : 'Inativo'}
                                size="small"
                                sx={{
                                  bgcolor: user.active ? '#dcfce7' : '#fee2e2',
                                  color: user.active ? '#166534' : '#991b1b',
                                  fontWeight: 600,
                                  fontSize: 11
                                }}
                              />
                            </td>
                            <td
                              style={{
                                ...tableCellStyle,
                                textAlign: 'center'
                              }}>
                              <Box
                                display="flex"
                                justifyContent="center"
                                gap={0.5}>
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenUserModal(user)}
                                    sx={{ color: '#125dae' }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    user.role?.protected
                                      ? 'Usuário protegido'
                                      : user.active
                                        ? 'Inativar'
                                        : 'Ativar'
                                  }>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleUser(user)}
                                      disabled={
                                        (user.role?.protected ?? false) ||
                                        saving
                                      }
                                      sx={{
                                        color: user.active
                                          ? '#dc2626'
                                          : '#16a34a'
                                      }}>
                                      {user.active ? (
                                        <BlockIcon fontSize="small" />
                                      ) : (
                                        <CheckCircleOutlineIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-end" mt={1.5}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenUserModal()}
                  sx={{
                    bgcolor: '#125dae',
                    '&:hover': { bgcolor: '#0e4a8a' },
                    fontSize: 13,
                    textTransform: 'none',
                    borderRadius: 2
                  }}>
                  Adicionar Usuário
                </Button>
              </Box>
            </Box>
          </Stack>
        )}
      </Styles.PageSection>

      {/* ── Role Modal ── */}
      <RoleModal
        open={roleModalOpen}
        editingRole={editingRole}
        onClose={() => setRoleModalOpen(false)}
        onSave={handleSaveRole}
        saving={saving}
      />

      {/* ── User Modal ── */}
      <UserModal
        open={userModalOpen}
        editingUser={editingUser}
        roles={roles}
        onClose={() => setUserModalOpen(false)}
        onSave={handleSaveUser}
        saving={saving}
      />

      {/* ── Snackbar feedback ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuthenticatedLayout>
  );
};

export default ConfigUsersScreen;
